/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { ToolEntry, ToolEntry$inboundSchema } from "./toolentry.js";

/**
 * A lightweight summary of a toolset, containing only the fields needed for org-level listing (e.g. RBAC UI).
 */
export type ToolsetSummary = {
  /**
   * When the toolset was created.
   */
  createdAt: Date;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  defaultEnvironmentSlug?: string | undefined;
  /**
   * The ID of the toolset
   */
  id: string;
  /**
   * Whether the toolset is enabled for MCP
   */
  mcpEnabled?: boolean | undefined;
  /**
   * Whether the toolset is public in MCP
   */
  mcpIsPublic?: boolean | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  mcpSlug?: string | undefined;
  /**
   * The name of the toolset
   */
  name: string;
  /**
   * The organization ID this toolset belongs to
   */
  organizationId: string;
  /**
   * The project ID this toolset belongs to
   */
  projectId: string;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug: string;
  /**
   * The mode to use for tool selection
   */
  toolSelectionMode: string;
  /**
   * The tools in this toolset
   */
  tools: Array<ToolEntry>;
  /**
   * When the toolset was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const ToolsetSummary$inboundSchema: z.ZodMiniType<
  ToolsetSummary,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    default_environment_slug: z.optional(z.string()),
    id: z.string(),
    mcp_enabled: z.optional(z.boolean()),
    mcp_is_public: z.optional(z.boolean()),
    mcp_slug: z.optional(z.string()),
    name: z.string(),
    organization_id: z.string(),
    project_id: z.string(),
    slug: z.string(),
    tool_selection_mode: z.string(),
    tools: z.array(ToolEntry$inboundSchema),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "default_environment_slug": "defaultEnvironmentSlug",
      "mcp_enabled": "mcpEnabled",
      "mcp_is_public": "mcpIsPublic",
      "mcp_slug": "mcpSlug",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "tool_selection_mode": "toolSelectionMode",
      "updated_at": "updatedAt",
    });
  }),
);

export function toolsetSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ToolsetSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolsetSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolsetSummary' from JSON`,
  );
}
